import { proxy } from 'valtio';

import { internalSites } from '@canvas/constants';
import { Deferrable } from '@canvas/deferrable';

export enum FeatureFlagSource {
  /** Flags used by our code but not managed by LD or Statsig */
  Local = 'local',
  /** Flags managed by LD. No new feature flags should be created using launchdarkly. All new
   *  flags must use Statsig moving forward.
   *  @see runbook: runbooks/how-to-create-a-feature-flag-statsig.md
   */
  DEPRECATED_LaunchDarkly = 'launchdarkly',
  /** Flags managed by Statsig */
  Statsig = 'statsig',
}

export type ClientFeatureFlagName = keyof typeof defaultFeatureFlagState;
export type ClientFeatureFlag =
  | {
      enabled: boolean;
      source: FeatureFlagSource.Local;
    }
  | {
      enabled: boolean;
      flag: string;
      source: FeatureFlagSource.DEPRECATED_LaunchDarkly | FeatureFlagSource.Statsig;
    };
export type ClientFeatureFlags = Record<ClientFeatureFlagName, ClientFeatureFlag>;

/**
 * All feature flags must be disabled by default.
 * @description Default feature flag values cannot be mutated.
 */
export const defaultFeatureFlagState = {
  /**
   * This flag enables/disables the ability to resize multiple entities at the same time.
   * Note that this will be a long standing hidden feature: All elements must be
   * resizeable with multi select before this can be enabled.
   * The flag in Launch darkly is archived. This is now a local only flag.
   */
  multiSelectResize: { enabled: false, source: FeatureFlagSource.Local },
  /**
   * Used for testing purposes. Do not use otherwise.
   * @deprecated
   */
  test: { enabled: false, source: FeatureFlagSource.Local },
  /**
   * Enables measuring content overflow using WebGL content (when visible)
   * instead of DOM for section titles
   * The flag in launch darkly is archived. This is now only a local flag.
   */
  webGlSectionTitleSize: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },
  /**
   * Enables reliability metrics reporting for users
   * @see https://app.launchdarkly.com/canvas/staging/features/should-enable-client-reliability-metrics
   */
  reliabilityMetrics: {
    enabled: false,
    flag: 'should-enable-client-reliability-metrics',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables inline comments
   * https://switcheroo.atlassian.com/gates/92238287-4e96-4b1a-ae1c-c904b4dbbf82
   */
  comments: {
    enabled: false,
    flag: 'confluence_whiteboard_comments_gate',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables timer sync
   * https://app.launchdarkly.com/canvas/staging/features/should-enable-timer-sync/targeting
   */
  timerSync: {
    enabled: false,
    flag: 'should-enable-timer-sync',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables the new Jira Parent Field for smart sections and jira issue create
   * @see https://app.launchdarkly.com/canvas/production/features/should-enable-jira-parent-field/targeting
   */
  shouldEnableJiraParentField: {
    enabled: false,
    flag: 'should-enable-jira-parent-field',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Shhh 🤫 (if you see this, DON'T search for it elsewhere in code to avoid spoilers!)
   * https://app.launchdarkly.com/canvas/staging/features/whiteboards-wargame/targeting
   */
  wargame: {
    enabled: false,
    flag: 'whiteboards-wargame',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables updates via http when websocket has had multiple failed connection attempts
   * https://app.launchdarkly.com/canvas/staging/features/should-update-via-http-when-websocket-wont-connect/targeting
   */
  shouldUpdateViaHttpWhenWebsocketWontConnect: {
    enabled: false,
    flag: 'should-update-via-http-when-websocket-wont-connect',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables the user to create a deep link to an entity that exists on the
   * whiteboard. When consuming this link, the whiteboard will focus the entity
   * on load
   * https://app.launchdarkly.com/canvas/staging/features/should-focus-on-element-on-load/targeting
   */
  shouldFocusOnElementOnLoad: {
    enabled: false,
    flag: 'should-focus-on-element-on-load',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables the ability import PDF files as images.
   * https://app.launchdarkly.com/canvas/staging/features/should-enable-pdf-import/targeting
   */
  pdfImport: {
    enabled: false,
    flag: 'should-enable-pdf-import',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables adding debug report to sentry errors
   * https://app.launchdarkly.com/canvas/staging/features/should-add-debug-report-to-sentry-errors/targeting
   */
  shouldAddDebugReportToSentryErrors: {
    enabled: false,
    flag: 'should-add-debug-report-to-sentry-errors',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables the V2 implementation of the timer
   * https://app.launchdarkly.com/canvas/staging/features/should-use-timer-v2/targeting
   */
  timerV2: {
    enabled: true,
    flag: 'should-use-timer-v2',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables using graphQL over REST for the session API (eg to get access and media tokens,
   * expiration, feature entitlements and the edition).
   * Note: is a local-only flag and SHOULD NOT have a DEPRECATED_LaunchDarkly flag as we don't want to block
   * app startup on the FF client
   */
  enableGraphqlSessionApi: {
    enabled: false,
    source: FeatureFlagSource.Local,
  },
  /**
   * Enables the ability export PDF files and other image formats.
   * https://app.launchdarkly.com/canvas/staging/features/should-enable-file-export/targeting
   */
  fileExport: {
    enabled: false,
    flag: 'should-enable-file-export',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Flag which enables the ability to import PDF files and parse their element data directly as
   * Confluence whiteboards elements
   * https://app.launchdarkly.com/canvas/production/features/pdf-importer/targeting
   */
  pdfImporter: {
    enabled: false,
    flag: 'pdf-importer',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Enables the ability to show sticky note authors
   * @see https://switcheroo.atlassian.com/gates/e88a6fe1-0847-4f2a-af11-168d42310b15
   */
  stickyNoteAuthor: {
    enabled: false,
    flag: 'should_enable_sticky_note_author',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the loom integration with whiteboards.
   * Note, the actual usage is also dependent on the confluence gate as well
   */
  whiteboardsLoomIntegration: {
    enabled: false,
    flag: 'whiteboardsloomintegration',
    source: FeatureFlagSource.Statsig,
  },

  /**
   * At present, the "Hide Cursors" button is not viewable by read only users
   * (users who have read only permissions or anonymous users). With this gate
   * enabled, the "Hide Cursors" button is visible to those users (ie in read
   * only mode).
   * https://switcheroo.atlassian.com/gates/879d03a4-6269-4158-bebf-781c746d0e97
   */
  allowHideCursorsForReadOnly: {
    enabled: false,
    flag: 'allow_hide_cursors_feature_for_read_only_users',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Change the url we call from the client to stargate to make use of the new
   * archetype stargate route
   * https://switcheroo.atlassian.com/gates/88ccf24a-d511-4a42-b627-9e77befa9d59
   */
  stargateRoutingUseArchetype: {
    enabled: false,
    flag: 'stargate_routing_use_archetype',
    source: FeatureFlagSource.Statsig,
  },
  // https://switcheroo.atlassian.com/gates/9fe2e71d-2001-492d-9719-03b0e99f9e94
  userPreferences: {
    enabled: false,
    flag: 'whiteboards-user-preferences',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the voting feature for whiteboards
   * https://switcheroo.atlassian.com/gates/e796b565-25bb-46f6-a445-fce4cf1cfb33
   */
  voting: {
    enabled: false,
    flag: 'should_enable_voting',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the ai generate stickies feature for whiteboards
   * https://switcheroo.atlassian.com/gates/0b2ca649-f67d-4014-851f-e58eb9f69789
   */
  aiGenerate: {
    enabled: false,
    flag: 'cc-whiteboards-ai-generate-stickies',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables the follow cursor feature for whiteboards
   * https://switcheroo.atlassian.com/gates/da69b40b-4a10-44dc-9aa6-d286bdf04eac
   */
  followCursor: {
    enabled: false,
    flag: 'should_enable_confluence_whiteboards_follow_cursor',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Enables transparent images to be displayed, using WebGL.
   * https://switcheroo.atlassian.com/gates/9e94685b-5fb7-483a-a7e0-b642175a3bc1
   */
  transparentImages: {
    enabled: false,
    flag: 'transparent_images',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * Kill switch for jsw plan on whiteboards experiment
   * https://app.launchdarkly.com/canvas/production/features/sprint-planning-whiteboards-kill-switch/targeting
   */
  sprintPlanningWhiteboardsKillSwitch: {
    enabled: false,
    flag: 'sprint-planning-whiteboards-kill-switch',
    source: FeatureFlagSource.DEPRECATED_LaunchDarkly,
  },
  /**
   * Align elements flag to enable aligning elements as a feature
   */
  alignElements: {
    enabled: false,
    flag: 'cc-whiteboards-align-elements',
    source: FeatureFlagSource.Statsig,
  },
  /**
   * A Feature Flag for UX Health week 26/06/2024
   * https://switcheroo.atlassian.com/ui/gates/54ee2321-4f19-44aa-858d-e3069e5b0494
   */
  uxHealthWeek: {
    enabled: false,
    flag: 'canvas-ux-health-week-june',
    source: FeatureFlagSource.Statsig,
  },
} as const satisfies Record<string, ClientFeatureFlag>;

type Mutable<T> = { -readonly [P in keyof T]: Mutable<T[P]> };

/**
 * Initial state is set using the `useFeatureFlags` hook in `@canvas/app`.
 */
export const currentFeatureFlagState: Mutable<ClientFeatureFlags> = proxy(
  defaultFeatureFlagState
);

/**
 * If true, supplied feature flag is enabled.
 * @description Ensure you check this value after app boot.
 * @param flag
 * @returns
 */
export const isFeatureFlagEnabled = (flag: ClientFeatureFlagName) => {
  return Boolean(currentFeatureFlagState[flag].enabled);
};

export const resetFeatureFlagState = () => {
  for (const featureFlag of Object.keys(
    defaultFeatureFlagState
  ) as (keyof typeof defaultFeatureFlagState)[]) {
    currentFeatureFlagState[featureFlag] = defaultFeatureFlagState[featureFlag];
  }
};

/**
 * Use this sparingly. Only to be used in cases where you truly, permanently want to allow a feature to be
 * used by the internal Atlassians (E.g. Showing banners, etc)
 */
export const isInternalSite = () =>
  internalSites
    .map((site) => site.hostname.toLowerCase())
    .concat(['canvas.localhost'])
    .includes(new URL(globalThis.location.href).hostname.toLowerCase());

export const launchDarklyClientInitialized = new Deferrable();
export const statsigClientInitalized = new Deferrable();

/**
 * A promise which resolves when the feature flag state has been initialized.
 * This will be resolved when both feature flag clients are ready.
 */
export const featureFlagsInitialized = Promise.all([
  launchDarklyClientInitialized.get(),
  statsigClientInitalized.get(),
]);
